import type { ChatStore } from "../Store";
import type { SubscriptionTransformerType } from "@/Modules/Transformers/Subscription";
import type { UserStore } from "@/Modules/User/Store";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";

import SubscriptionApiConector from "@/Services/TherapyApi/Subscriptions";
import SubscriptionTransformer from "@/Modules/Transformers/Subscription";
import { createAllAgreement, getAgreements } from "@/Services/TherapyApi/LegalAgreements";

import useUserStore from "@/Modules/User/Store";

import { hasValue } from "@/Utils/Objects";

class LoadChatRoomUserActionHandler {
  constructor(
    private readonly chatStore: ChatStore,
    private readonly userStore: UserStore = useUserStore(),
    private readonly subscriptionApi = new SubscriptionApiConector(),
    private readonly subscriptionTransformer: SubscriptionTransformerType = SubscriptionTransformer
  ) {}

  public async handle(chatRoomId: string): Promise<void> {
    this.chatStore.rooms[chatRoomId].user.isLoadingUser = true;

    const chatRoomUserId = this.chatStore.rooms[chatRoomId].user.id;
    const userId = this.userStore.user?.id;

    if (hasValue(userId)) {
      await Promise.all([
        this.createLegalAgreement(chatRoomUserId, userId),
        this.loadSubscription(chatRoomUserId, chatRoomId),
        this.loadLegalAgreements(chatRoomUserId, chatRoomId),
      ]);
    }

    this.chatStore.rooms[chatRoomId].user.isLoadingUser = false;
  }

  private async createLegalAgreement(chatRoomUserId: string, userId: string): Promise<void> {
    try {
      await createAllAgreement(chatRoomUserId, userId);
    } catch (error: unknown) {
      this.handleGenericError();
    }
  }

  private async loadSubscription(chatRoomUserId: string, chatRoomId: string): Promise<void> {
    try {
      const subscriptionApi = await this.subscriptionApi.getSubscription(chatRoomUserId);
      const subscription = this.subscriptionTransformer.toDomain(subscriptionApi);

      if (subscription) {
        this.chatStore.rooms[chatRoomId].user.subscription = subscription;
      }
    } catch (error: unknown) {
      this.handleGenericError();
    }
  }

  private async loadLegalAgreements(chatRoomUserId: string, chatRoomId: string): Promise<void> {
    try {
      const userId = this.userStore.user ? this.userStore.user.id : "";
      const legalAgreementsApi = await getAgreements(chatRoomUserId, userId);
      const legalAgreements = legalAgreementsApi ? legalAgreementsApi : [];

      if (hasValue(legalAgreements)) {
        this.chatStore.rooms[chatRoomId].user.legalAgreements = legalAgreements;
      } else {
        this.chatStore.rooms[chatRoomId].user.legalAgreements = [];
      }
    } catch (error: unknown) {
      this.handleGenericError();
    }
  }

  private handleGenericError(): void {
    this.userStore.notification = {
      type: NotificationType.ERROR,
      message: "there_was_an_error_connecting_to_the_server_please_try_again_later",
      code: "generic error",
    };
  }
}

export default LoadChatRoomUserActionHandler;
